import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import api from "./api/axios";

export const STORAGE_KEY = "jv.businessContext";
export const BusinessContext = createContext(null);
const Context = BusinessContext;
class HierarchyError extends Error {}
export function getHuntingYear(date = new Date()) {
  return date.getMonth() >= 3 ? date.getFullYear() : date.getFullYear() - 1;
}
export function defaultBusinessYear(years, date = new Date()) {
  const current = years.find(y => String(y.BetriebsjahrKurz) === String(getHuntingYear(date)));
  return current || [...years].sort((a, b) => Number(b.BetriebsjahrKurz) - Number(a.BetriebsjahrKurz))[0] || null;
}
export function businessError(error, fallback) {
  return error.response?.data?.error || fallback;
}
export function mandantUrl(url, mandantId) {
  if (!Number.isSafeInteger(Number(mandantId)) || Number(mandantId) <= 0) {
    throw new Error("Bitte einen Mandanten auswählen.");
  }
  const [path, query = ""] = url.split("?");
  const params = new URLSearchParams(query);
  params.set("mandant_id", mandantId);
  return `${path}?${params.toString()}`;
}
export function contextUrl(url, ids) {
  if (![ids.mandantId, ids.betriebId, ids.betriebsjahrId].every(id => Number.isSafeInteger(Number(id)) && Number(id) > 0)) {
    throw new Error("Bitte Mandant, Betrieb und Betriebsjahr vollständig auswählen.");
  }
  const [path, query = ""] = url.split("?");
  const params = new URLSearchParams(query);
  params.delete("jagdjahr");
  params.set("mandant_id", ids.mandantId);
  params.set("betrieb_id", ids.betriebId);
  params.set("betriebsjahr_id", ids.betriebsjahrId);
  return `${path}?${params.toString()}`;
}

const initial = { mandantId: null, betriebId: null, betriebsjahrId: null,
  mandanten: [], betriebe: [], betriebsjahre: [], loading: true, error: "" };
const byId = (rows, id) => rows.find(row => String(row.Id) === String(id));

export function BusinessProvider({ children }) {
  const [state, setState] = useState(initial);
  const generation = useRef(0);
  const lastAction = useRef(null);
  const run = useCallback(async (action) => {
    lastAction.current = action;
    const job = ++generation.current;
    const update = patch => { if (generation.current === job) setState(previous => ({ ...previous, ...patch })); };
    const fetchRows = async url => {
      const response = await api.get(url);
      if (generation.current !== job) return null;
      if (!Array.isArray(response.data)) throw new HierarchyError("Ungültige Hierarchieantwort.");
      return response.data;
    };
    update({ loading: true, error: "", betriebsjahrId: null, betriebsjahre: [],
      ...(action.stage === "year" ? {} : { betriebId: null, betriebe: [] }),
      ...(action.stage === "start" ? { mandantId: null, mandanten: [] } : {}) });
    try {
      let mandanten = action.mandanten;
      if (action.stage === "start") {
        mandanten = await fetchRows("/mandanten");
        if (!mandanten) return;
        mandanten = mandanten.filter(m => m.Status === "active");
        update({ mandanten });
      }
      const storedMandant = byId(mandanten, action.mandantId);
      const mandant = action.stage === "start" ? storedMandant || mandanten[0] : storedMandant;
      if (!mandant) throw new HierarchyError("Kein aktiver Mandant verfügbar.");
      update({ mandantId: mandant.Id });
      let betriebe = action.betriebe;
      if (action.stage !== "year") {
        betriebe = await fetchRows(`/betriebe?mandant_id=${mandant.Id}`);
        if (!betriebe) return;
        betriebe = betriebe.filter(b => String(b.Mandant_Id) === String(mandant.Id));
        update({ betriebe });
      }
      const restoreBetrieb = action.stage === "start" && storedMandant;
      const betrieb = action.stage === "year" ? byId(betriebe, action.betriebId)
        : (restoreBetrieb ? byId(betriebe, action.betriebId) : null) || betriebe[0];
      if (!betrieb) throw new HierarchyError("Für diesen Mandanten ist kein Betrieb verfügbar.");
      update({ betriebId: betrieb.Id });
      const years = await fetchRows(`/betriebsjahre?betrieb_id=${betrieb.Id}`);
      if (!years) return;
      const betriebsjahre = years.filter(y => String(y.Mandant_Id) === String(mandant.Id) && String(y.Betrieb_Id) === String(betrieb.Id));
      const restoreYear = restoreBetrieb && String(betrieb.Id) === String(action.betriebId);
      const year = (restoreYear ? byId(betriebsjahre, action.betriebsjahrId) : null) || defaultBusinessYear(betriebsjahre);
      update({ betriebsjahre });
      if (!year) throw new HierarchyError("Für diesen Betrieb ist kein Betriebsjahr verfügbar.");
      update({ betriebsjahrId: year.Id, loading: false });
    } catch (error) {
      if (generation.current === job) update({ loading: false, error: businessError(error,
        error instanceof HierarchyError ? error.message : "Geschäftskontext konnte nicht geladen werden. Bitte erneut versuchen.") });
    }
  }, []);

  useEffect(() => {
    let saved = {};
    try { saved = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}; } catch { /* Invalid storage uses validated defaults. */ }
    run({ stage: "start", mandantId: saved.mandantId, betriebId: saved.betriebId, betriebsjahrId: saved.betriebsjahrId });
    return () => { generation.current += 1; };
  }, [run]);
  const selectedMandant = byId(state.mandanten, state.mandantId) || null;
  const selectedBetrieb = byId(state.betriebe, state.betriebId) || null;
  const selectedBetriebsjahr = byId(state.betriebsjahre, state.betriebsjahrId) || null;
  const ready = Boolean(selectedMandant && selectedBetrieb && selectedBetriebsjahr && !state.loading && !state.error);
  useEffect(() => {
    if (!selectedMandant) return;
    // Keep explicit parent selection during failures; a reload must not silently
    // return to a previously selected Mandant merely because children failed.
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ mandantId: selectedMandant.Id, betriebId: selectedBetrieb?.Id ?? null, betriebsjahrId: selectedBetriebsjahr?.Id ?? null })); } catch { /* Selection still works when storage is unavailable. */ }
  }, [selectedMandant, selectedBetrieb, selectedBetriebsjahr]);

  const withContext = useCallback(url => contextUrl(url, state), [state]);
  const params = useMemo(() => ready ? { mandant_id: state.mandantId, betrieb_id: state.betriebId, betriebsjahr_id: state.betriebsjahrId } : null,
    [ready, state.mandantId, state.betriebId, state.betriebsjahrId]);
  const scopeKey = `${state.mandantId}:${state.betriebId}:${state.betriebsjahrId}`;
  const value = { ...state, ready, selectedMandant, selectedBetrieb, selectedBetriebsjahr, params, scopeKey, withContext,
    selectMandant: id => { setState(s => ({ ...s, mandantId: Number(id), betriebId: null, betriebsjahrId: null, betriebe: [], betriebsjahre: [] })); run({ stage: "betrieb", mandanten: state.mandanten, mandantId: id }); },
    selectBetrieb: id => { setState(s => ({ ...s, betriebId: Number(id), betriebsjahrId: null, betriebsjahre: [] })); run({ stage: "year", mandanten: state.mandanten, mandantId: state.mandantId, betriebe: state.betriebe, betriebId: id }); },
    selectBetriebsjahr: id => { ++generation.current; const valid = byId(state.betriebsjahre, id); setState(s => ({ ...s, betriebsjahrId: valid?.Id ?? null, loading: false, error: valid ? "" : "Ungültiges Betriebsjahr." })); },
    retry: () => run(lastAction.current || { stage: "start" }),
  };
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useBusinessContext() {
  const context = useContext(Context);
  if (!context) throw new Error("BusinessProvider fehlt.");
  return context;
}
